import { GridView as GridViewIcon } from "@mui/icons-material";
import { Box, Button, Card, Link, Stack, Tooltip, Typography } from "@mui/material";
import { EditableVariableList } from "../../../../components/editable-variable-list";
import { useUrlModal } from "../../../../hooks/use-url-dialog";
import type { Test, TestMatrixOption, TestVariable } from "../../../../types";
import { MatrixCsvImportDialog } from "../matrix-csv-import-dialog";

interface MatrixTabProps {
  test: Test | undefined;
  isUpdatingTest: boolean;
  isMutating: boolean;
  onAddMatrixOption: () => void;
  onRunMatrix: () => void;
  onRunMatrixOption: (optionId: string) => void;
  onDeleteMatrixOption: (optionId: string) => void;
  onUpdateMatrixOption: (optionId: string, variables: TestVariable[]) => void;
  onImportMatrixOptions: (options: TestMatrixOption[]) => void;
}

export function MatrixTab({
  test,
  isUpdatingTest,
  isMutating,
  onAddMatrixOption,
  onRunMatrix,
  onRunMatrixOption,
  onDeleteMatrixOption,
  onUpdateMatrixOption,
  onImportMatrixOptions,
}: MatrixTabProps) {
  const [importOpen, setImportOpen] = useUrlModal("matrix-import");
  const existingCount = test?.matrix?.options?.length ?? 0;
  const isEmpty = existingCount === 0;

  const sampleLink = (
    <Link
      href="/matrix-options-example.csv"
      download="matrix-options-example.csv"
      underline="hover"
      variant="caption"
      color="text.secondary"
    >
      Download sample CSV
    </Link>
  );

  return (
    <>
      {!isEmpty && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          {sampleLink}
          <Tooltip title="Upload a CSV to replace all matrix options" placement="top">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setImportOpen(true)}
              disabled={isUpdatingTest}
              aria-label="Import matrix options from CSV"
            >
              Import CSV
            </Button>
          </Tooltip>
          <Tooltip title="Add a single matrix option" placement="top">
            <Button
              size="small"
              variant="outlined"
              onClick={onAddMatrixOption}
              disabled={isUpdatingTest}
              aria-label="Add matrix option"
            >
              Add option
            </Button>
          </Tooltip>
          <Tooltip title="Trigger a complete test matrix run" placement="top">
            <Button
              size="small"
              onClick={onRunMatrix}
              disabled={isMutating}
              aria-label="Run complete test matrix"
            >
              Run matrix
            </Button>
          </Tooltip>
        </Stack>
      )}
      {isEmpty && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            py: 8,
            px: 2,
            gap: 1.5,
            color: "text.secondary",
          }}
        >
          <GridViewIcon sx={{ fontSize: 48, opacity: 0.4 }} />
          <Typography variant="subtitle1">No matrix options yet</Typography>
          <Typography variant="body2" sx={{ maxWidth: 420 }}>
            Matrix options let this test run against multiple variable sets. Add one manually, or
            import a CSV where each row becomes an option.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setImportOpen(true)}
              disabled={isUpdatingTest}
            >
              Import CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={onAddMatrixOption}
              disabled={isUpdatingTest}
            >
              Add option
            </Button>
          </Stack>
          <Box sx={{ mt: 0.5 }}>{sampleLink}</Box>
        </Box>
      )}
      <Stack spacing={1}>
        {test?.matrix?.options.map((opt, ix) => (
          <Card key={ix} sx={{ p: 2 }}>
            <Stack direction="column" spacing={1} alignItems="stretch">
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2">{`Option ${ix + 1}`}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onRunMatrixOption(opt.id)}
                    disabled={isMutating}
                    aria-label={`Run matrix option ${opt.id}`}
                  >
                    Run this option
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onDeleteMatrixOption(opt.id)}
                    disabled={isUpdatingTest}
                    aria-label={`Delete matrix option ${opt.id}`}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
              <EditableVariableList
                hideTitle
                variables={opt.var_options}
                isLoading={isUpdatingTest}
                onVariablesChange={(vars) => onUpdateMatrixOption(opt.id, vars)}
              />
            </Stack>
          </Card>
        ))}
      </Stack>
      <MatrixCsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        existingCount={existingCount}
        onImport={onImportMatrixOptions}
        isSubmitting={isUpdatingTest}
      />
    </>
  );
}
